import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GlobalLoaderState } from '../models/bulk-search.models';

@Injectable({
  providedIn: 'root'
})
export class GlobalLoaderService {
  /** Start time when progress was initialized; used to compute elapsed on frontend (avoids backend fluctuation). */
  private progressStartTimeMs: number | null = null;

  private loaderStateSubject = new BehaviorSubject<GlobalLoaderState>({
    showProgress: false,
    progressPercentage: 0,
    completedTabs: 0,
    totalTabs: 0,
    completedTabNames: [],
    currentTab: '',
    estimatedTimeRemaining: null,
    elapsedTimeSeconds: null,
    overviewStagesMode: false,
    overviewBackendStage: null,
    servedFromCache: false
  });

  public loaderState$: Observable<GlobalLoaderState> = this.loaderStateSubject.asObservable();

  /**
   * Initialize progress tracking for the global loader
   * @param totalTabs Total tabs (e.g. 7 for analysis tabs, audits excluded)
   * @param overviewStagesMode Whether overview sub-steps are shown
   */
  initializeProgress(totalTabs: number = 9, overviewStagesMode = false): void {
    this.progressStartTimeMs = Date.now();
    this.loaderStateSubject.next({
      showProgress: true,
      progressPercentage: 0,
      completedTabs: 0,
      totalTabs: totalTabs,
      completedTabNames: [],
      currentTab: '',
      estimatedTimeRemaining: null,
      elapsedTimeSeconds: 0,
      overviewStagesMode,
      overviewBackendStage: null
    });
  }

  /**
   * Update progress information
   */
  updateProgress(
    progressPercentage: number,
    completedTabs: number,
    completedTabNames: string[] = [],
    currentTab: string = '',
    estimatedTimeRemaining: number | null = null,
    totalTabs?: number,
    overviewBackendStage?: { step: number; total: number; label: string } | null,
    elapsedTimeSeconds?: number | null,
    servedFromCache?: boolean
  ): void {
    const currentState = this.loaderStateSubject.value;
    if (currentState.showProgress) {
      const nextStage =
        overviewBackendStage !== undefined
          ? overviewBackendStage
          : currentState.overviewBackendStage;
      // Elapsed: from frontend when we have a start time (avoids backend fluctuation); else use passed/current
      const elapsedSeconds =
        this.progressStartTimeMs != null
          ? Math.floor((Date.now() - this.progressStartTimeMs) / 1000)
          : (elapsedTimeSeconds !== undefined ? elapsedTimeSeconds : currentState.elapsedTimeSeconds);
      this.loaderStateSubject.next({
        ...currentState,
        progressPercentage,
        completedTabs,
        totalTabs: totalTabs !== undefined ? totalTabs : currentState.totalTabs,
        completedTabNames,
        currentTab,
        estimatedTimeRemaining,
        elapsedTimeSeconds: elapsedSeconds ?? currentState.elapsedTimeSeconds,
        overviewStagesMode: currentState.overviewStagesMode,
        overviewBackendStage: nextStage ?? null,
        servedFromCache: servedFromCache ?? currentState.servedFromCache ?? false
      });
    }
  }

  /**
   * Update only the current tab being processed
   */
  updateCurrentTab(currentTab: string): void {
    const currentState = this.loaderStateSubject.value;
    if (currentState.showProgress) {
      this.loaderStateSubject.next({
        ...currentState,
        currentTab
      });
    }
  }

  /**
   * Update only the progress percentage
   */
  updateProgressPercentage(progressPercentage: number): void {
    const currentState = this.loaderStateSubject.value;
    if (currentState.showProgress) {
      this.loaderStateSubject.next({
        ...currentState,
        progressPercentage
      });
    }
  }

  /**
   * Reset the loader state
   */
  resetLoader(): void {
    this.progressStartTimeMs = null;
    this.loaderStateSubject.next({
      showProgress: false,
      progressPercentage: 0,
      completedTabs: 0,
      totalTabs: 0,
      completedTabNames: [],
      currentTab: '',
      estimatedTimeRemaining: null,
      elapsedTimeSeconds: null,
      overviewStagesMode: false,
      overviewBackendStage: null,
      servedFromCache: false
    });
  }

  /**
   * Get current loader state
   */
  getCurrentState(): GlobalLoaderState {
    return this.loaderStateSubject.value;
  }
}
